import axios from "axios";
import Services from "./Services";
import { decorateSportsnewFixtureListRequest } from './_helpers/sportsnewListTransport';
import { captureSportsnewAccountCarrier } from './_helpers/sportsnewAccountCarrier';
import { createSportsnewPlacementTransport } from './_helpers/sportsnewPlacementTransport';

/** Sportsnew API service — mirrors /sportsnew/* endpoints */
export class SportsnewService extends Services {
  constructor(init) {
    super(init);
    this._name = "SPORTSNEW";
    const placementTransport = createSportsnewPlacementTransport();
    this.placementAxios = placementTransport.client;
    this.requestPlacement = (url, config, carrier) => placementTransport.request(url, {
      ...config, signal: config?.signal || this.controller.signal,
    }, carrier);
    return this;
  }

  capturePlacementAuthSessionCarrier = captureSportsnewAccountCarrier;

  /* ── Discovery & config ── */
  getStatus = async () =>
    await this.decorate(async () => await this.axios("/sportsnew/status", { method: "GET" }));

  getCategories = async () =>
    await this.decorate(async () => await this.axios("/sportsnew/categories", { method: "GET" }));

  getLeagues = async (sport) =>
    await this.decorate(async () => await this.axios(`/sportsnew/leagues/${sport}`, { method: "GET" }));

  getDisplaySettings = async () =>
    await this.decorate(async () => await this.axios("/sportsnew/display-settings", { method: "GET" }));

  getBettingPolicy = async () =>
    await this.decorate(async () => await this.axios("/sportsnew/betting-policy", { method: "GET" }));

  getComboBonus = async () =>
    await this.decorate(async () => await this.axios("/sportsnew/combo-bonus", { method: "GET" }));

  getBettingLimits = async (params) =>
    await this.decorate(async () => await this.axios("/sportsnew/betting-limits", { method: "GET", params }));

  /* ── Fixture counts ── */
  getCounts = async (params) =>
    await this.decorate(async () => await this.axios("/sportsnew/counts", { method: "GET", params }));

  getLiveCount = async (params) =>
    await this.decorate(async () => await this.axios("/sportsnew/live-count", { method: "GET", params }));

  getMatchCounts = async (params) =>
    await this.decorate(async () => await this.axios("/sportsnew/match-counts", { method: "GET", params }));

  /* ── Navigation tree ── */
  getNavigation = async (params) =>
    await this.decorate(async () => await this.axios("/sportsnew/navigation", { method: "GET", params }));

  /* ── Fixture lists ── */
  getFixtures = async (params, { cacheBuster, signal } = {}) => {
    const requestParams = { ...params, _sportsnew_presentation: '1',
      ...(cacheBuster == null ? {} : { _sportsnew_list_retry: cacheBuster }) };
    return await decorateSportsnewFixtureListRequest(async () => await this.axios("/sportsnew/fixtures", {
      method: "GET",
      params: requestParams,
      ...(signal ? { signal } : {}),
    }));
  };

  getFeatured = async (params) =>
    await this.decorate(async () => await this.axios("/sportsnew/featured", { method: "GET", params }));

  getFeaturedLeagues = async (params) =>
    await this.decorate(async () => await this.axios("/sportsnew/featured-leagues", { method: "GET", params }));

  /* ── Single fixture ── */
  getFixture = async (fixtureId) =>
    await this.decorate(async () => await this.axios(`/sportsnew/fixtures/${fixtureId}`, { method: "GET" }));

  getFixtureMarkets = async (fixtureId, params = {}, options = {}) =>
    await this.decorate(async () => await this.axios(`/sportsnew/fixtures/${fixtureId}/markets`, { method: "GET", params, ...options }));

  // Optional broadcast metadata uses Crypto's existing auth/backoff client.
  // Keep a slow, cancelled or stale broadcast outside the betting error state.
  getFixtureBroadcasts = async (fixtureId, { signal, isCurrentRequest } = {}) => {
    const current = () => !signal?.aborted && (!isCurrentRequest || isCurrentRequest() === true);
    if (!current()) return { status: false, aborted: true };
    try {
      const response = await this.axios(`/sportsnew/fixtures/${encodeURIComponent(fixtureId)}/broadcasts`, {
        method: "GET", signal, timeout: 2000,
      });
      return current() ? response.data : { status: false, aborted: true };
    } catch (error) {
      return { status: false, aborted: !current() || axios.isCancel(error), code: error.response?.status ?? null };
    }
  };

  /* ── Bets ── */
  previewBet = async (data) =>
    await this.decorate(async () => await this.axios("/sportsnew/bets/preview", { method: "POST", data }));

  issuePlacementReceipt = async ({ idempotencyKey, ...data }, { authSessionCarrier } = {}) =>
    await this.decorate(async () => await this.requestPlacement("/sportsnew/bets/placement-attempts/receipt", {
      method: "POST",
      data: { ...data, idempotencyKey },
      headers: { 'Idempotency-Key': idempotencyKey },
    }, authSessionCarrier));

  placeBet = async ({ idempotencyKey, ...data }, { authSessionCarrier } = {}) =>
    await this.decorate(async () => await this.requestPlacement("/sportsnew/bets", {
      method: "POST",
      data: { ...data, idempotencyKey },
      headers: { 'Idempotency-Key': idempotencyKey },
    }, authSessionCarrier));

  resolvePlacementAttempt = async (attempt, { authSessionCarrier } = {}) =>
    await this.decorate(async () => await this.requestPlacement("/sportsnew/bets/placement-attempt-resolution", {
      method: "POST",
      data: {
        idempotencyKey: attempt?.idempotencyKey,
        challenge: attempt?.challenge,
        accountGeneration: attempt?.accountGeneration,
        placementReceipt: attempt?.placementReceipt,
      },
      headers: { 'Idempotency-Key': attempt?.idempotencyKey },
    }, authSessionCarrier));

  getBets = async (params) =>
    await this.decorate(async () => await this.axios("/sportsnew/bets", { method: "GET", params }));

  getBet = async (publicId) =>
    await this.decorate(async () => await this.axios(`/sportsnew/bets/${publicId}`, { method: "GET" }));

  cancelBet = async (publicId) =>
    await this.decorate(async () => await this.axios(`/sportsnew/bets/${publicId}/cancel`, { method: "POST" }));

  /* ── Recovery ── */
  recoverFixture = async (fixtureId, params = {}) =>
    await this.decorate(async () => await this.axios(`/sportsnew/recovery/fixtures/${fixtureId}`, { method: "GET", params }));

  recoverLists = async (params) =>
    await this.decorate(async () => await this.axios("/sportsnew/recovery/lists", { method: "GET", params }));

  recoverSelected = async (data) =>
    await this.decorate(async () => await this.axios("/sportsnew/recovery/selected", { method: "POST", data }));

  requestSnapshotRecovery = async (fixtureId) =>
    await this.decorate(async () => await this.axios(`/sportsnew/recovery/fixtures/${fixtureId}`, { method: "POST" }));
}

export default SportsnewService;
